import { useState, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Alert,
  Box,
  Typography,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  IconButton,
} from '@mui/material';
import { Copy } from 'lucide-react';
import { useCreateUser } from '@/lib/query/hooks/useUsers';
import { useCompanyAccounts } from '@/lib/query/hooks/useCompanies';
import {
  useMiseSellersCurrent,
  useMiseSellerGroupsCurrent,
  useMiseSellerSellerGroupMapCurrent,
} from '@/lib/query/hooks/useMiseSellers';
import { useMiseSellers, useMiseSellerGroups } from '@/lib/query/hooks/useMiseAccounts';
import { indentSellerGroupLabel, sortSellerGroupsByTree } from '@/lib/mise/sellerGroupTree';
import { buildSellerTreeOptions } from '@/lib/mise/sellerTree';
import { findMappedSellerGroupId } from '@/lib/mise/sellerGroupMapping';
import { SellerTreeOptionList } from '@/components/mise/SellerTreeOptionList';
import { EXCLUDED_SELLER_ID } from '@/lib/types/mise/seller';
import type { CreateUserRequest } from '@/lib/types/mise/user';

interface CreateUserDialogProps {
  open: boolean;
  onClose: () => void;
  companyId: string;
}

export function CreateUserDialog({ open, onClose, companyId }: CreateUserDialogProps) {
  const [userEmail, setUserEmail] = useState('');
  const [userName, setUserName] = useState('');
  const [defaultAccountId, setDefaultAccountId] = useState('');
  const [sellerId, setSellerId] = useState('');
  const [comparisonType, setComparisonType] = useState<'seller' | 'seller_group'>('seller');
  const [comparisonSellerId, setComparisonSellerId] = useState('');
  const [comparisonSellerGroupId, setComparisonSellerGroupId] = useState('');
  const [suppressInvitationEmail, setSuppressInvitationEmail] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [resultDialogOpen, setResultDialogOpen] = useState(false);
  const [temporaryPassword, setTemporaryPassword] = useState('');
  const [copied, setCopied] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const createUserMutation = useCreateUser(companyId);
  const { data: accounts = [], isLoading: accountsLoading } = useCompanyAccounts(companyId);

  // 店舗一覧の取得（sellers_current優先、snapshot_hot.sellersフォールバック）
  const { data: sellersCurrent = [], isLoading: sellersCurrentLoading } =
    useMiseSellersCurrent(defaultAccountId);
  const { data: sellersSnapshot = [], isLoading: sellersSnapshotLoading } =
    useMiseSellers(defaultAccountId);

  const sellers = useMemo(() => {
    const source = sellersCurrent.length > 0 ? sellersCurrent : sellersSnapshot;
    return source.filter((s) => s.id !== EXCLUDED_SELLER_ID);
  }, [sellersCurrent, sellersSnapshot]);

  // 店舗・店舗グループを階層表示するための階層データ（snapshot_hot 由来）
  const { data: sellerGroupNodes = [], isLoading: sellerGroupNodesLoading } =
    useMiseSellerGroups(defaultAccountId);

  const sellersLoading = defaultAccountId
    ? sellersCurrentLoading || sellersSnapshotLoading || sellerGroupNodesLoading
    : false;

  // 店舗を所属する店舗グループの階層の下に並べる（所属は snapshot_hot.sellers から引く）
  const sellerTreeOptions = useMemo(
    () => buildSellerTreeOptions(sellers, sellerGroupNodes, sellersSnapshot),
    [sellers, sellerGroupNodes, sellersSnapshot],
  );

  // 店舗グループ一覧の取得
  const { data: sellerGroups = [], isLoading: sellerGroupsCurrentLoading } =
    useMiseSellerGroupsCurrent(defaultAccountId);
  const sellerGroupsLoading = sellerGroupsCurrentLoading || sellerGroupNodesLoading;

  // 同名の店舗グループを見分けられるよう、階層順に並べて字下げする
  const sellerGroupOptions = useMemo(
    () => sortSellerGroupsByTree(sellerGroups, sellerGroupNodes),
    [sellerGroups, sellerGroupNodes],
  );

  // 店舗を選んだら、その店舗グループ（生成AIの比較分析と同じ比較先）を比較店舗グループの初期値にする
  const { data: sellerGroupMap = [], isLoading: sellerGroupMapLoading } =
    useMiseSellerSellerGroupMapCurrent(defaultAccountId);
  // マッピングが届く前に店舗を選ぶと初期値が入らないため、届くまで店舗を選ばせない
  const sellerSelectLoading = sellersLoading || (!!defaultAccountId && sellerGroupMapLoading);
  const mappedSellerGroupId = findMappedSellerGroupId(sellerGroupMap, sellerId);

  // バリデーション
  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!userEmail.trim()) {
      newErrors.userEmail = 'メールアドレスは必須です';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(userEmail)) {
      newErrors.userEmail = '有効なメールアドレスを入力してください';
    }

    if (!userName.trim()) {
      newErrors.userName = 'ユーザー名は必須です';
    }

    if (!defaultAccountId) {
      newErrors.defaultAccountId = 'デフォルトアカウントは必須です';
    }

    if (!sellerId) {
      newErrors.sellerId = '店舗は必須です';
    }

    if (comparisonType === 'seller' && !comparisonSellerId) {
      newErrors.comparisonSeller = '比較店舗は必須です';
    }
    if (comparisonType === 'seller_group' && !comparisonSellerGroupId) {
      newErrors.comparisonSellerGroup = '比較店舗グループは必須です';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ダイアログを閉じる
  const handleClose = () => {
    if (createUserMutation.isPending) {
      return; // 処理中は閉じられない
    }
    setUserEmail('');
    setUserName('');
    setDefaultAccountId('');
    setSellerId('');
    setComparisonType('seller');
    setComparisonSellerId('');
    setComparisonSellerGroupId('');
    setSuppressInvitationEmail(false);
    setErrors({});
    setConfirmDialogOpen(false);
    setResultDialogOpen(false);
    setTemporaryPassword('');
    setCopied(false);
    onClose();
  };

  // 作成ボタン押下時（確認ダイアログを表示）
  const handleCreateClick = () => {
    if (!validate()) {
      return;
    }
    setConfirmDialogOpen(true);
  };

  // 確認ダイアログで確定
  const handleConfirmCreate = async () => {
    if (!validate()) {
      setConfirmDialogOpen(false);
      return;
    }

    const request: CreateUserRequest = {
      user_email: userEmail.trim(),
      user_name: userName.trim(),
      default_account_id: defaultAccountId,
      seller_id: sellerId,
      comparison_seller_id: comparisonType === 'seller' ? comparisonSellerId : undefined,
      comparison_seller_group_id:
        comparisonType === 'seller_group' ? comparisonSellerGroupId : undefined,
      suppress_invitation_email: suppressInvitationEmail || undefined,
    };

    try {
      const result = await createUserMutation.mutateAsync(request);
      if (result.temporary_password) {
        setTemporaryPassword(result.temporary_password);
        setConfirmDialogOpen(false);
        setResultDialogOpen(true);
      } else {
        handleClose();
      }
    } catch (error) {
      // エラーはmutationのonErrorで処理される
      console.error('ユーザー作成に失敗しました:', error);
    }
  };

  // 仮パスワード表示ダイアログを閉じる
  const handleResultClose = () => {
    setResultDialogOpen(false);
    handleClose();
  };

  // 仮パスワードをクリップボードにコピー
  const handleCopyPassword = async () => {
    await navigator.clipboard.writeText(temporaryPassword);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // 選択されたアカウントの情報を取得
  const selectedAccount = accounts.find((acc) => acc.id === defaultAccountId);
  // 選択された店舗の情報を取得
  const selectedSeller = sellers.find((s) => s.id === sellerId);
  // 選択された比較店舗/比較店舗グループの情報を取得
  const selectedComparisonSeller = sellers.find((s) => s.id === comparisonSellerId);
  const selectedComparisonSellerGroup = sellerGroups.find((g) => g.id === comparisonSellerGroupId);

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>ユーザーを追加</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            {createUserMutation.isError && (
              <Alert severity="error">
                {createUserMutation.error instanceof Error
                  ? createUserMutation.error.message
                  : 'ユーザーの作成に失敗しました。'}
              </Alert>
            )}

            <TextField
              label="メールアドレス"
              type="email"
              value={userEmail}
              onChange={(e) => {
                setUserEmail(e.target.value);
                if (errors.userEmail) {
                  setErrors({ ...errors, userEmail: '' });
                }
              }}
              error={!!errors.userEmail}
              helperText={errors.userEmail}
              required
              fullWidth
              disabled={createUserMutation.isPending}
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={suppressInvitationEmail}
                  onChange={(e) => setSuppressInvitationEmail(e.target.checked)}
                  disabled={createUserMutation.isPending}
                />
              }
              label="招待メールを送信しない"
            />

            <TextField
              label="ユーザー名"
              value={userName}
              onChange={(e) => {
                setUserName(e.target.value);
                if (errors.userName) {
                  setErrors({ ...errors, userName: '' });
                }
              }}
              error={!!errors.userName}
              helperText={errors.userName}
              required
              fullWidth
              disabled={createUserMutation.isPending}
            />

            <TextField
              select
              label="デフォルトアカウント"
              value={defaultAccountId}
              onChange={(e) => {
                setDefaultAccountId(e.target.value);
                setSellerId('');
                setComparisonSellerId('');
                setComparisonSellerGroupId('');
                if (errors.defaultAccountId) {
                  setErrors({ ...errors, defaultAccountId: '', sellerId: '' });
                }
              }}
              error={!!errors.defaultAccountId}
              helperText={errors.defaultAccountId}
              required
              fullWidth
              disabled={createUserMutation.isPending || accountsLoading}
            >
              {accountsLoading ? (
                <MenuItem disabled>読み込み中...</MenuItem>
              ) : accounts.length === 0 ? (
                <MenuItem disabled>アカウントがありません</MenuItem>
              ) : (
                accounts.map((account) => (
                  <MenuItem key={account.id} value={account.id}>
                    {account.name || account.id} {account.code && `(${account.code})`}
                  </MenuItem>
                ))
              )}
            </TextField>

            {defaultAccountId && (
              <TextField
                select
                label="店舗"
                value={sellerId}
                onChange={(e) => {
                  setSellerId(e.target.value);
                  const groupId = findMappedSellerGroupId(sellerGroupMap, e.target.value);
                  if (groupId) {
                    setComparisonType('seller_group');
                    setComparisonSellerId('');
                    setComparisonSellerGroupId(groupId);
                  } else if (
                    mappedSellerGroupId &&
                    comparisonSellerGroupId === mappedSellerGroupId
                  ) {
                    // 前の店舗から自動で入れた値は、別の店舗の比較先として残さない
                    setComparisonSellerGroupId('');
                  }
                  if (errors.sellerId) {
                    setErrors({ ...errors, sellerId: '' });
                  }
                }}
                error={!!errors.sellerId}
                helperText={errors.sellerId}
                required
                fullWidth
                disabled={createUserMutation.isPending || sellerSelectLoading}
                slotProps={{
                  inputLabel: { shrink: true },
                  select: { native: true },
                }}
              >
                <option value="">
                  {sellerSelectLoading
                    ? '読み込み中...'
                    : sellers.length === 0
                      ? '店舗がありません'
                      : '選択してください'}
                </option>
                <SellerTreeOptionList options={sellerTreeOptions} />
              </TextField>
            )}

            {defaultAccountId && (
              <>
                <FormControl component="fieldset">
                  <FormLabel component="legend">比較店舗の指定方法 *</FormLabel>
                  <RadioGroup
                    row
                    value={comparisonType}
                    onChange={(e) => {
                      const value = e.target.value as 'seller' | 'seller_group';
                      setComparisonType(value);
                      setComparisonSellerId('');
                      setComparisonSellerGroupId('');
                      setErrors({
                        ...errors,
                        comparisonSeller: '',
                        comparisonSellerGroup: '',
                      });
                    }}
                  >
                    <FormControlLabel
                      value="seller"
                      control={<Radio disabled={createUserMutation.isPending} />}
                      label="比較店舗で指定"
                    />
                    <FormControlLabel
                      value="seller_group"
                      control={<Radio disabled={createUserMutation.isPending} />}
                      label="比較店舗グループで指定"
                    />
                  </RadioGroup>
                </FormControl>

                {comparisonType === 'seller' && (
                  <TextField
                    select
                    label="比較店舗"
                    value={comparisonSellerId}
                    onChange={(e) => {
                      setComparisonSellerId(e.target.value);
                      if (errors.comparisonSeller) {
                        setErrors({ ...errors, comparisonSeller: '' });
                      }
                    }}
                    error={!!errors.comparisonSeller}
                    helperText={errors.comparisonSeller}
                    required
                    fullWidth
                    disabled={createUserMutation.isPending || sellersLoading}
                    slotProps={{
                      inputLabel: { shrink: true },
                      select: { native: true },
                    }}
                  >
                    <option value="">
                      {sellersLoading
                        ? '読み込み中...'
                        : sellers.length === 0
                          ? '店舗がありません'
                          : '選択してください'}
                    </option>
                    <SellerTreeOptionList options={sellerTreeOptions} />
                  </TextField>
                )}

                {comparisonType === 'seller_group' && (
                  <TextField
                    select
                    label="比較店舗グループ"
                    value={comparisonSellerGroupId}
                    onChange={(e) => {
                      setComparisonSellerGroupId(e.target.value);
                      if (errors.comparisonSellerGroup) {
                        setErrors({ ...errors, comparisonSellerGroup: '' });
                      }
                    }}
                    error={!!errors.comparisonSellerGroup}
                    helperText={
                      errors.comparisonSellerGroup ||
                      (mappedSellerGroupId && comparisonSellerGroupId === mappedSellerGroupId
                        ? '店舗の店舗グループマッピングから自動で入れました'
                        : undefined)
                    }
                    required
                    fullWidth
                    disabled={createUserMutation.isPending || sellerGroupsLoading}
                    slotProps={{
                      inputLabel: { shrink: true },
                      select: { native: true },
                    }}
                  >
                    <option value="">
                      {sellerGroupsLoading
                        ? '読み込み中...'
                        : sellerGroups.length === 0
                          ? '店舗グループがありません'
                          : '選択してください'}
                    </option>
                    {sellerGroupOptions.map(({ group, depth }) => (
                      <option key={group.id} value={group.id}>
                        {indentSellerGroupLabel(depth, group.name || group.id)}
                      </option>
                    ))}
                  </TextField>
                )}
              </>
            )}

            {accounts.length === 0 && !accountsLoading && (
              <Alert severity="warning">
                この企業に紐づくアカウントがありません。ユーザー登録には少なくとも1つのアカウントが必要です。
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={createUserMutation.isPending}>
            キャンセル
          </Button>
          <Button
            onClick={handleCreateClick}
            variant="contained"
            disabled={
              createUserMutation.isPending ||
              accountsLoading ||
              accounts.length === 0 ||
              !userEmail.trim() ||
              !userName.trim() ||
              !defaultAccountId ||
              !sellerId ||
              (comparisonType === 'seller' && !comparisonSellerId) ||
              (comparisonType === 'seller_group' && !comparisonSellerGroupId)
            }
          >
            作成
          </Button>
        </DialogActions>
      </Dialog>

      {/* 確認ダイアログ */}
      <Dialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        sx={{
          '& .MuiDialog-container': {
            zIndex: 1400, // Material-UIのDialogより高いz-indexを設定
          },
        }}
      >
        <DialogTitle>ユーザーを登録しますか？</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <Typography>
              {suppressInvitationEmail
                ? '以下の内容でユーザーを登録します。メール送信は行われません。作成後に仮パスワードが画面に表示されます。初回ログイン時に新しいパスワードへの変更が必要です。'
                : '以下の内容でユーザーを登録します。登録完了後、登録したメールアドレスに一時パスワードが送信されます。初回ログイン時に新しいパスワードへの変更が必要です。'}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography>
                <strong>メールアドレス:</strong> {userEmail}
              </Typography>
              <Typography>
                <strong>ユーザー名:</strong> {userName}
              </Typography>
              <Typography>
                <strong>デフォルトアカウント:</strong>{' '}
                {selectedAccount ? selectedAccount.name || selectedAccount.id : defaultAccountId}
              </Typography>
              <Typography>
                <strong>店舗:</strong> {selectedSeller?.name || sellerId}
              </Typography>
              {comparisonType === 'seller' && (
                <Typography>
                  <strong>比較店舗:</strong> {selectedComparisonSeller?.name || comparisonSellerId}
                </Typography>
              )}
              {comparisonType === 'seller_group' && (
                <Typography>
                  <strong>比較店舗グループ:</strong>{' '}
                  {selectedComparisonSellerGroup?.name || comparisonSellerGroupId}
                </Typography>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirmDialogOpen(false)}
            disabled={createUserMutation.isPending}
          >
            キャンセル
          </Button>
          <Button
            onClick={handleConfirmCreate}
            variant="contained"
            disabled={createUserMutation.isPending}
          >
            {createUserMutation.isPending ? '登録中...' : '登録する'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 仮パスワード表示ダイアログ */}
      <Dialog open={resultDialogOpen} maxWidth="sm" fullWidth>
        <DialogTitle>ユーザーを作成しました</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <code
                style={{
                  padding: '8px 12px',
                  backgroundColor: '#f5f5f5',
                  borderRadius: '4px',
                  fontSize: '1.1rem',
                  fontFamily: 'monospace',
                  flex: 1,
                  wordBreak: 'break-all',
                }}
              >
                {temporaryPassword}
              </code>
              <IconButton onClick={handleCopyPassword} size="small" title="コピー">
                <Copy size={18} />
              </IconButton>
              {copied && (
                <Typography variant="body2" color="success.main">
                  コピーしました
                </Typography>
              )}
            </Box>
            <Alert severity="warning">このダイアログを閉じると仮パスワードは再表示できません</Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleResultClose} variant="contained">
            閉じる
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
